import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faFilter } from '@fortawesome/pro-regular-svg-icons';
import { faFilter as faFilterSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { ControlledKeywordSearch } from '../ControlledKeywordSearch';
import { Filter, FilterGroup } from './FilterGroup';
import { SortedBy } from './sortBy';

type Value = {
  searchValue: string;
  filtersValue: URLSearchParams;
  placeholder?: string;
};

type Props = {
  filters: Filter[];
  value: Value;
  onChange: (value: Value) => void;
  placement?: 'portal';
  placeholder?: string;
  mobileActionButton?: {
    label: string;
    onClick: () => void;
  };

  /**
   * Keyword search is currently broken on Activity view so have added this option to hide it
   */
  hideKeywordSearch?: boolean;

  // Add sort options
  sortOptions?: Array<{ key: SortedBy; name: string }>;
  sortBy?: SortedBy;
  onSortChange?: (value: SortedBy) => void;

  // Pass nav scroll detection state to FilterSelect components
  navScrollDetectionEnabled?: boolean;

  // View switcher
  viewSwitcher?: React.ReactNode;
};

/**
 * Renders a keyword search input and a filter group.
 *
 * **IMPORTANT**: Changes to the vertical spacing (padding, margins) of this component
 * when rendered within a `NavContainer.FilterRow` might affect the overall height
 * and require updating the KNOWN_COLLAPSED_FILTER_HEIGHT constant in
 * `src/hooks/useDynamicHeaderMargin.ts`.
 */
export function FilterGroupSearch({
  filters,
  value,
  onChange,
  placement,
  hideKeywordSearch,
  sortOptions,
  sortBy,
  onSortChange,
  placeholder,
  mobileActionButton,
  navScrollDetectionEnabled,
  viewSwitcher,
}: Props) {
  const [showFilters, setShowFilters] = useState(true);
  const [hasBeenToggled, setHasBeenToggled] = useState(false);
  const { isMobile } = useBreakpoint();

  // Material Design animation constants
  const filterAnimations = {
    initial: {
      opacity: 0,
      y: -20,
      scale: 0.95,
      transformOrigin: 'top center',
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.25,
        ease: [0.4, 0.0, 0.2, 1],
      },
    },
    exit: {
      opacity: 0,
      y: -10,
      scale: 0.98,
      transition: {
        duration: 0.2,
        ease: [0.4, 0.0, 1, 1],
      },
    },
  };

  // Determine when to show the filter group section
  const shouldShowFilterGroup = !isMobile || showFilters || !!hideKeywordSearch;

  // Handle toggle with animation tracking
  const handleToggle = () => {
    setHasBeenToggled(true);
    setShowFilters(!showFilters);
  };

  return (
    <div
      className={classNames(
        // Ensure consistent bottom spacing on mobile even when keyword search is hidden
        isMobile && 'pb-2',
        'w-full max-w-full overflow-hidden border-x border-primary-container bg-primary-container transition-all lg:flex lg:w-auto lg:items-center lg:justify-start lg:border-x-0 lg:bg-white lg:py-[7px]'
      )}
    >
      {!hideKeywordSearch && (
        <div className='mb-1 flex flex-1 gap-2 pl-4 pr-3 lg:mb-0 lg:pl-0'>
          <ControlledKeywordSearch
            placeholder={placeholder}
            value={value.searchValue}
            onChange={(query) => onChange({ ...value, searchValue: query })}
          />
          {/* Only show toggle on mobile when search is not hidden */}
          {isMobile && (
            <>
              <FilterToggleButton
                filtersValue={value.filtersValue}
                onToggle={handleToggle}
                isOpen={showFilters}
              />
              {mobileActionButton && (
                <button
                  className='rounded-full border border-white border-opacity-50 px-4 py-2 text-white active:border-opacity-10 active:bg-black active:bg-opacity-10'
                  onClick={() => {
                    mobileActionButton?.onClick();
                  }}
                >
                  {mobileActionButton?.label}
                </button>
              )}{' '}
              {viewSwitcher}
            </>
          )}
        </div>
      )}

      <AnimatePresence mode='wait'>
        {shouldShowFilterGroup && (
          <motion.div
            className='z-10 ml-1 w-full lg:overflow-x-auto'
            // Apply animation only on mobile when filters are toggled AND toggle has been used
            initial={
              isMobile && !hideKeywordSearch && hasBeenToggled
                ? filterAnimations.initial
                : undefined
            }
            animate={
              isMobile && !hideKeywordSearch && hasBeenToggled
                ? filterAnimations.animate
                : undefined
            }
            exit={
              isMobile && !hideKeywordSearch && hasBeenToggled
                ? filterAnimations.exit
                : undefined
            }
          >
            <FilterGroup
              filters={filters}
              value={value.filtersValue}
              onChange={(filters) => {
                onChange({ ...value, filtersValue: filters });
              }}
              placement={placement}
              sortByOptions={sortOptions}
              sortBy={sortBy}
              sortByOnChange={onSortChange}
              navScrollDetectionEnabled={navScrollDetectionEnabled}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function FilterToggleButton({
  filtersValue,
  onToggle,
  isOpen,
}: {
  filtersValue: URLSearchParams;
  onToggle: () => void;
  isOpen: boolean;
}) {
  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const currentView = pathParts[2] || '';
  const isScheduledView = currentView === 'scheduled';
  const isCompletedView = currentView === 'completed';

  // Count active filters (excluding internal flags)
  let activeFilterCount = 0;
  let hasScheduledDateFilter = false;
  let hasCompletedDateFilter = false;

  for (const [key, value] of filtersValue.entries()) {
    // Skip internal flags and empty values
    if (key.startsWith('_') || !value || value === '') {
      continue;
    }

    // Explicitly ignore the phantom 'warehouse' key from stock-on-hand
    if (key === 'warehouse') {
      continue;
    }

    // Check for date filters to count them as a single filter per view
    if (key === 'scheduledDateAfter' || key === 'scheduledDateBefore') {
      hasScheduledDateFilter = true;
      continue;
    }

    if (key === 'completedDateAfter' || key === 'completedDateBefore') {
      hasCompletedDateFilter = true;
      continue;
    }

    // Count all other filters normally
    activeFilterCount++;
  }

  // Add date filters to the count, but only for the current view
  if (hasScheduledDateFilter && isScheduledView) activeFilterCount++;
  if (hasCompletedDateFilter && isCompletedView) activeFilterCount++;

  const hasActiveFilters = activeFilterCount > 0;

  return (
    <div
      className='relative flex h-[42px] w-[42px] cursor-pointer items-center justify-center rounded-full bg-white bg-opacity-20'
      onClick={onToggle}
    >
      <FontAwesomeIcon
        icon={
          hasActiveFilters
            ? (faFilterSolid as IconProp)
            : (faFilter as IconProp)
        }
        className='h-5 w-5 text-white'
      />

      {(isOpen || hasActiveFilters) && (
        <div className='absolute bottom-1.5 right-1.5 flex h-[17px] w-[17px] items-center justify-center rounded-full border border-primary-container border-opacity-80 bg-white text-primary-container'>
          {isOpen ? (
            <span className='text-xs font-bold'>-</span>
          ) : (
            <span className='text-xs'>{activeFilterCount}</span>
          )}
        </div>
      )}
    </div>
  );
}
